import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { TimePicker } from "components/designV2/timePicker/timePicker";
import type { ComponentProps } from "react";
import type {
  EditableRangeModel,
  ReportedTimeCardModel,
} from "./timeReportDetailsPanel.reportedTimeCard.contract";
import {
  DataCard,
  DataCardLoadingOverlay,
  LoadingSpinner,
  Section,
  SectionHeaderLeft,
  SectionHeaderRow,
  SectionMeta,
  SectionTitle,
} from "./timeReportDetailsPanel.styled";
import { DetailsRow } from "./detailsRow.component";
import { EditableRangeRow } from "./editableRangeRow.component";
import { TotalTimeRow } from "./totalTimeRow.component";

type TimePickerOnChange = ComponentProps<typeof TimePicker>["onChange"];

interface ReportedTimeCardHandlers {
  onEnterEditMode: () => void;
  onReportedStartChange: TimePickerOnChange;
  onReportedEndChange: TimePickerOnChange;
  onBreakStartChange: TimePickerOnChange;
  onBreakEndChange: TimePickerOnChange;
}

interface ReportedTimeCardProps {
  model: ReportedTimeCardModel;
  handlers: ReportedTimeCardHandlers;
}

export const ReportedTimeCard = (props: ReportedTimeCardProps): JSX.Element => {
  const { model, handlers } = props;
  const isLoading = model.state.loadStatus === "loading";
  const hasError = model.state.loadStatus === "error";

  const renderEditableRange = (
    range: EditableRangeModel,
    onStartChange: TimePickerOnChange,
    onEndChange: TimePickerOnChange,
  ): JSX.Element => {
    if (model.state.isEditMode) {
      return (
        <EditableRangeRow
          label={range.label}
          fromLabel={model.labels.fromLabel}
          toLabel={model.labels.toLabel}
          startValue={range.startValue}
          endValue={range.endValue}
          onStartChange={onStartChange}
          onEndChange={onEndChange}
        />
      );
    }

    return <DetailsRow label={range.label} value={range.readValue} />;
  };

  return (
    <Section>
      <SectionHeaderRow>
        <SectionHeaderLeft>
          <SectionTitle>{model.header.sectionTitle}</SectionTitle>
          <SectionMeta>{model.header.sectionMeta}</SectionMeta>
        </SectionHeaderLeft>
        {model.header.showEditButton && (
          <SecondaryButton
            type="button"
            size="small"
            iconName="v2/pen"
            label={model.header.editLabel}
            disabled={model.header.isEditButtonDisabled}
            onClick={handlers.onEnterEditMode}
          />
        )}
      </SectionHeaderRow>
      <DataCard>
        {isLoading && (
          <DataCardLoadingOverlay>
            <LoadingSpinner />
          </DataCardLoadingOverlay>
        )}
        <DetailsRow
          label={model.rows.date.label}
          value={model.rows.date.value}
        />
        {renderEditableRange(
          model.rows.reported,
          handlers.onReportedStartChange,
          handlers.onReportedEndChange,
        )}
        <DetailsRow
          label={model.rows.stamped.label}
          value={model.rows.stamped.value}
        />
        <DetailsRow
          label={model.rows.planned.label}
          value={model.rows.planned.value}
        />
        <TotalTimeRow
          label={model.rows.totalTime.label}
          totalValue={model.rows.totalTime.totalValue}
          overtimeLabel={model.rows.totalTime.overtimeValue}
        />
        {renderEditableRange(
          model.rows.breakTime,
          handlers.onBreakStartChange,
          handlers.onBreakEndChange,
        )}
        {hasError && (
          <DetailsRow
            label={model.labels.oopsLabel}
            value={model.labels.loadErrorLabel}
          />
        )}
      </DataCard>
    </Section>
  );
};
